import React from 'react';
import { Filter, SortAsc } from 'lucide-react';

const FilterSidebar = ({ filters, setFilters, tasks }) => {
    // Extract unique tags from all tasks
    const allTags = [...new Set(tasks.flatMap(task => task.tags || []))];

    const toggleTag = (tag) => {
        const newTags = filters.tags.includes(tag)
            ? filters.tags.filter(t => t !== tag)
            : [...filters.tags, tag];
        setFilters({ ...filters, tags: newTags });
    };

    const clearFilters = () => {
        setFilters({
            priority: 'all',
            status: 'all',
            tags: [],
            sortBy: filters.sortBy
        });
    };

    const hasActiveFilters =
        filters.priority !== 'all' ||
        filters.status !== 'all' ||
        filters.tags.length > 0;

    return (
        <div className="space-y-4">
            {/* Filter Header */}
            <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                        <Filter size={18} />
                        Filters
                    </h3>
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="text-xs text-accent hover:text-accent/80 transition-colors"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                {/* Priority Filter */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                        Priority
                    </label>
                    <select
                        value={filters.priority}
                        onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent transition-colors"
                    >
                        <option value="all">All Priorities</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                </div>

                {/* Status Filter */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                        Status
                    </label>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent transition-colors"
                    >
                        <option value="all">All Status</option>
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="done">Done</option>
                    </select>
                </div>

                {/* Tags Filter */}
                {allTags.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                            Tags
                        </label>
                        <div className="space-y-2">
                            {allTags.map(tag => (
                                <label key={tag} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={filters.tags.includes(tag)}
                                        onChange={() => toggleTag(tag)}
                                        className="rounded border-white/20 bg-white/5 text-accent focus:ring-accent"
                                    />
                                    <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                                        {tag}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Sort Options */}
            <div className="glass-card p-4">
                <h3 className="font-semibold text-white flex items-center gap-2 mb-4">
                    <SortAsc size={18} />
                    Sort By
                </h3>
                <div className="space-y-2">
                    {[
                        { value: 'dueDate', label: 'Due Date' },
                        { value: 'priority', label: 'Priority' },
                        { value: 'title', label: 'Title (A-Z)' }
                    ].map(option => (
                        <label key={option.value} className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="sortBy"
                                value={option.value}
                                checked={filters.sortBy === option.value}
                                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                                className="text-accent focus:ring-accent"
                            />
                            <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                                {option.label}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Stats */}
            <div className="glass-card p-4">
                <h3 className="font-semibold text-white mb-3">Statistics</h3>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-white/60">Total Tasks</span>
                        <span className="font-medium text-white">{tasks.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-white/60">Completed</span>
                        <span className="font-medium text-green-400">
                            {tasks.filter(t => t.status === 'done').length}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-white/60">In Progress</span>
                        <span className="font-medium text-blue-400">
                            {tasks.filter(t => t.status === 'in-progress').length}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-white/60">To Do</span>
                        <span className="font-medium text-yellow-400">
                            {tasks.filter(t => t.status === 'todo').length}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSidebar;
